import { useId, useState, type ElementType, type MouseEvent, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";

import { cn } from "../lib/cn";

const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, label, summary, [role="button"], [role="combobox"], [role="switch"], [role="tab"], [data-collapsible-ignore]';

export interface CollapsibleSectionProps {
  title: ReactNode;
  children: ReactNode;
  accessibleName?: string;
  subtitle?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  as?: ElementType;
  headingAs?: ElementType;
  collapsible?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: "icon" | "header";
  /** Section layout avoids a second card around already framed content. */
  variant?: "card" | "section";
  className?: string;
  headerClassName?: string;
  titleClassName?: string;
  trailingClassName?: string;
  contentClassName?: string;
}

export function CollapsibleSection({
  title,
  children,
  accessibleName,
  subtitle,
  leading,
  trailing,
  as: Container = "section",
  headingAs: Heading = "h2",
  collapsible = true,
  open: controlledOpen,
  defaultOpen = true,
  onOpenChange,
  trigger = "header",
  variant = "card",
  className,
  headerClassName,
  titleClassName,
  trailingClassName,
  contentClassName,
}: CollapsibleSectionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const contentId = useId();
  const headingId = useId();
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = (nextOpen: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const toggle = () => setOpen(!open);
  // With a header that holds its own controls the header cannot be one <button>. The chevron
  // stays the keyboard/screen-reader control; a pointer click on any inert part of the header
  // toggles too, while a click on a control inside it (button, link, field…) keeps its own job.
  const toggleFromHeader = (event: MouseEvent<HTMLElement>) => {
    if ((event.target as Element).closest(INTERACTIVE_SELECTOR)) return;
    if (window.getSelection()?.toString()) return;
    toggle();
  };
  const chevron = collapsible ? (
    <ChevronRight
      className={cn(
        "h-4 w-4 shrink-0 text-muted transition-[transform,color] group-hover/header:text-primary motion-reduce:transition-none",
        open && "rotate-90",
      )}
      aria-hidden
    />
  ) : null;

  const headerContent = (
    <>
      {trigger === "header" ? chevron : null}
      {leading}
      <div className="flex min-w-0 flex-1 items-baseline gap-2">
        <Heading
          id={headingId}
          className={cn("min-w-0 font-semibold text-primary", titleClassName)}
        >
          {title}
        </Heading>
        {subtitle ? <div className="truncate text-xs text-muted">{subtitle}</div> : null}
      </div>
      {trailing ? (
        <div className={cn("ml-auto flex shrink-0 items-center", trailingClassName)}>
          {trailing}
        </div>
      ) : null}
    </>
  );

  return (
    <Container
      aria-labelledby={headingId}
      className={cn(
        variant === "card"
          ? "overflow-hidden rounded-lg border border-border bg-surface"
          : "min-w-0 border-t border-border",
        className,
      )}
    >
      {collapsible && trigger === "header" ? (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={toggle}
          className={cn(
            "flex w-full items-center gap-2 text-left transition-colors hover:bg-surface-elevated",
            variant === "card"
              ? "border-b border-border bg-surface-elevated px-3 py-2.5"
              : "flex-wrap bg-transparent px-0 py-4",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
            headerClassName,
          )}
        >
          {headerContent}
        </button>
      ) : (
        <div
          onClick={collapsible ? toggleFromHeader : undefined}
          className={cn(
            "flex min-h-10 items-center gap-2",
            collapsible && "group/header cursor-pointer",
            variant === "card"
              ? "border-b border-border bg-surface-elevated px-3 py-2"
              : "flex-wrap bg-transparent px-0 py-4",
            headerClassName,
          )}
        >
          {collapsible ? (
            <button
              type="button"
              onClick={toggle}
              className="-ml-1 flex min-h-8 min-w-8 items-center justify-center rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              aria-expanded={open}
              aria-controls={contentId}
              aria-label={`${open ? "Collapse" : "Expand"} ${accessibleName ?? (typeof title === "string" ? title : "section")}`}
            >
              {chevron}
            </button>
          ) : null}
          {headerContent}
        </div>
      )}

      {open ? (
        <div id={contentId} className={contentClassName}>
          {children}
        </div>
      ) : null}
    </Container>
  );
}
